/**
 * Formats a numeric amount with an ISO 4217 currency code for display.
 * Falls back to `CODE amount` with locale grouping if the code is invalid or unsupported.
 */
export function formatCurrencyAmount(amount: unknown, currencyCode: string): string {
  const parsed =
    typeof amount === "number" && Number.isFinite(amount) ? amount : Number(amount);
  const value = Number.isFinite(parsed) ? parsed : 0;
  const trimmed = (currencyCode ?? "").trim();
  const upper = trimmed.toUpperCase();

  if (/^[A-Z]{3}$/.test(upper)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: upper,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      // fall through
    }
  }

  const label = trimmed || "USD";
  return `${label} ${value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * ISO 4217 code + grouped amount only (no localized currency symbol), e.g. `NGN 1,234`.
 */
export function formatCurrencyIsoCodeAndAmount(
  amount: unknown,
  currencyCode: string
): string {
  const parsed =
    typeof amount === "number" && Number.isFinite(amount) ? amount : Number(amount);
  const value = Number.isFinite(parsed) ? parsed : 0;
  const code = (currencyCode ?? "USD").trim().toUpperCase() || "USD";
  return `${code} ${value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}
